import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'
import { Button } from '../../atoms/button/button'
import { Icon } from '../../atoms/icon/icon'

/**
 * Navigation (463:51055): the white action bar at the foot of a work-order screen
 * (drawn 1220px, the content column; e.g. 541:15949). Its boolean properties are
 * `Back`, `Secondary button` and `Primary button`; here each one shows when its
 * prop is given.
 *
 * Back is a 40px circle with a 1.25px Stroke ring and the accent arrow-left-01.
 * The Figma pads it 25px / 26.667px inside a 40px box, more than the box holds;
 * its drawn result is the 26.667px arrow 5.833px from the left edge (0.833px left
 * of centre, 463:51046) and centred vertically, which is what this renders. The bar is
 * the Figma's justify-between auto-layout, so with Back hidden the buttons are its
 * only child and sit on the left, as the component lays them out.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const navigationVariants = cva(
	'flex w-full items-center justify-between bg-atlas-light px-10 py-3 font-atlas-sans',
)

/** One of the bar's buttons. */
export interface NavigationAction {
	label: string
	onPress?: () => void
	isDisabled?: boolean
}

export interface NavigationProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	/** Shows the back button and is called when it is pressed. */
	onBack?: () => void
	backLabel?: string
	/** The outlined button, e.g. "Save Draft". */
	secondaryAction?: NavigationAction
	/** The filled button, e.g. "Send Task". */
	primaryAction?: NavigationAction
}

/** A back button and the screen's call-to-action buttons; every press is a callback. */
export function Navigation({
	onBack,
	backLabel = 'Back',
	secondaryAction,
	primaryAction,
	className,
	...props
}: NavigationProps) {
	return (
		<div {...props} className={cn(navigationVariants(), className)}>
			{onBack ? (
				<AriaButton
					aria-label={backLabel}
					onPress={onBack}
					className={cn(
						'flex size-10 shrink-0 cursor-pointer items-center justify-start rounded-atlas-full pl-atlas-back-arrow-x inset-ring-(length:--atlas-border-width-back) inset-ring-atlas-stroke',
						focusRing,
					)}
				>
					<Icon name="arrow-left-01" />
				</AriaButton>
			) : null}
			{secondaryAction || primaryAction ? (
				<div className="flex shrink-0 items-center gap-3">
					{secondaryAction ? (
						<Button variant="secondary" onPress={secondaryAction.onPress} isDisabled={secondaryAction.isDisabled}>
							{secondaryAction.label}
						</Button>
					) : null}
					{primaryAction ? (
						<Button variant="primary" onPress={primaryAction.onPress} isDisabled={primaryAction.isDisabled}>
							{primaryAction.label}
						</Button>
					) : null}
				</div>
			) : null}
		</div>
	)
}
